import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarCheck, Container, MapPin, Medal, Phone, ReceiptText, Repeat, Truck, Building2, Home as HomeIcon } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import DumpsterCard from "../components/DumpsterCard";
import ReviewCarousel from "../components/ReviewCarousel";
import ServiceAreaMap from "../components/ServiceAreaMap";
import CTASection from "../components/CTASection";
import { useRequestModal } from "../components/RequestModal";
import { BUSINESS, ROLLOFF_SIZES, SERVICE_AREAS, TRUST_ITEMS } from "../data/site";
import { track } from "../lib/api";

const TRUST_ICONS = [MapPin, Medal, Repeat, CalendarCheck, ReceiptText];

function HeroLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { openRequest } = useRequestModal();
  return (
    <>
      <SEO
        title="Roll-Off Dumpster Rentals in Yavapai County, AZ"
        description="Reliable roll-off dumpster rentals and front-load waste service across Yavapai County. Locally owned, veteran owned. Same-Day Swaps Available. No hidden fees."
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Roll-Off Dumpster Rental",
          provider: { "@type": "LocalBusiness", name: BUSINESS.name, telephone: BUSINESS.phone },
          areaServed: SERVICE_AREAS.map((a) => a.name),
        }}
      />

      {/* 1. Split-screen hero */}
      <section className="relative overflow-hidden bg-white" data-testid="hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-3.5 py-1.5 mb-6"
            >
              <MapPin className="h-3.5 w-3.5" /> Locally & Veteran Owned · Prescott, AZ
            </motion.p>
            <h1 className="font-heading font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05]">
              <HeroLine delay={0.15}>Reliable Roll-Off</HeroLine>
              <HeroLine delay={0.28}>Dumpster Rentals Across</HeroLine>
              <HeroLine delay={0.41}><span className="text-sky-600">Yavapai County</span></HeroLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              From construction sites and long-term contractor accounts to one-time projects, Yavapai Waste Solutions delivers dependable dumpster service — Same-Day Swaps Available, Saturday Swaps Available, and straightforward pricing.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.68, duration: 0.5 }}
              className="mt-8 hidden sm:flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => openRequest()}
                className="px-8 py-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-heading font-semibold text-base transition-all active:scale-[0.98] shadow-lg shadow-sky-600/20"
                data-testid="hero-request-dumpster"
              >
                Request a Dumpster
              </button>
              <a
                href={BUSINESS.phoneHref}
                onClick={() => track("click_to_call", { location: "hero" })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border-2 border-slate-900 text-slate-900 font-heading font-semibold hover:bg-slate-900 hover:text-white transition-all active:scale-[0.98]"
                data-testid="hero-call"
              >
                <Phone className="h-4 w-4" /> Call for Same-Day Service
              </a>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="mt-5 hidden sm:block text-sm text-slate-500">
              Contractor? <Link to="/contractor-dumpster-rentals#pricing" className="font-semibold text-sky-700 hover:text-sky-800 underline underline-offset-4" data-testid="hero-contractor-link">Ask about custom pricing for ongoing service.</Link>
            </motion.p>
          </div>
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-3 bg-sky-100/60 rounded-2xl rotate-1" aria-hidden="true" />
              <img
                src="/assets/yws/uploads/rolloff-hero.jpeg"
                alt="Branded YWS roll-off dumpster from Yavapai Waste Solutions"
                className="relative rounded-2xl w-full object-cover aspect-[16/10] shadow-2xl"
                data-testid="hero-image"
                fetchpriority="high"
              />
              <div className="absolute -bottom-5 left-5 bg-white rounded-xl shadow-xl border border-slate-100 px-5 py-3.5 flex items-center gap-3">
                <Truck className="h-6 w-6 text-sky-600" />
                <div>
                  <p className="font-heading font-bold text-slate-900 text-sm leading-tight">Same-Day Swaps Available</p>
                  <p className="text-xs text-slate-500">Saturday Swaps Available</p>
                </div>
              </div>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="mt-9 sm:hidden text-sm text-slate-500">
              Contractor? <Link to="/contractor-dumpster-rentals#pricing" className="font-semibold text-sky-700 hover:text-sky-800 underline underline-offset-4" data-testid="hero-contractor-link-mobile">Ask about custom pricing for ongoing service.</Link>
            </motion.p>
          </div>
        </div>
      </section>

      {/* 2. Trust bar */}
      <section className="border-y border-slate-200 bg-slate-50 overflow-hidden" data-testid="trust-bar" aria-label="Why customers trust YWS">
        <div className="marquee py-5">
          <div className="marquee-track flex w-max">
            {[0, 1].map((copy) => (
              <ul key={copy} className="flex items-center gap-x-12 pr-12" aria-hidden={copy === 1}>
                {TRUST_ITEMS.map((t, i) => {
                  const Icon = TRUST_ICONS[i];
                  return (
                    <li key={t} className="flex items-center gap-2 text-sm font-heading font-semibold text-slate-700 whitespace-nowrap">
                      <Icon className="h-4 w-4 text-sky-600" /> {t}
                    </li>
                  );
                })}
              </ul>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Three main services */}
      <section className="py-16 sm:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-700 mb-3">What do you need?</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Three Services. One Local Team.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Reveal className="lg:row-span-1">
              <Link
                to="/roll-off-dumpsters"
                className="group flex flex-col h-full rounded-2xl border-2 border-sky-600 bg-white overflow-hidden shadow-lg shadow-sky-600/10 hover:shadow-2xl transition-all hover:-translate-y-1"
                data-testid="service-rolloff"
              >
                <div className="relative">
                  <img src="/assets/yws/uploads/rolloff-truck-delivery.jpeg" alt="YWS roll-off truck delivering a branded 30-yard dumpster" className="w-full aspect-[16/9] object-cover" loading="lazy" />
                  <span className="absolute top-3 left-3 bg-sky-600 text-white text-[11px] font-heading font-semibold uppercase tracking-wider px-3 py-1 rounded-full">Primary Service</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Container className="h-6 w-6 text-sky-600" />
                    <h3 className="font-heading font-bold text-xl text-slate-900">Roll-Off Dumpster Rentals</h3>
                  </div>
                  <p className="text-slate-600 text-sm">Construction, contractors & one-time projects. 20–40 yard sizes, no maximum rental period.</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-heading font-semibold text-sky-700 group-hover:gap-2.5 transition-all">
                    Explore Roll-Off <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/front-load/residential"
                className="group flex flex-col h-full rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid="service-residential"
              >
                <img src="/assets/yws/uploads/front-load-6-2.jpeg" alt="Branded YWS front-load dumpster for residential service" className="w-full aspect-[16/9] object-cover" loading="lazy" />
                <div className="p-6 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <HomeIcon className="h-6 w-6 text-sky-600" />
                    <h3 className="font-heading font-bold text-xl text-slate-900">Residential Front-Load</h3>
                  </div>
                  <p className="text-slate-600 text-sm">Reliable recurring waste service with flexible pickup schedules. 3–8 yard sizes.</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-heading font-semibold text-sky-700 group-hover:gap-2.5 transition-all">
                    Residential Service <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/front-load/commercial"
                className="group flex flex-col h-full rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid="service-commercial"
              >
                <img src="/assets/yws/uploads/fl-truck-dumping-commercial.jpeg" alt="YWS front-load truck servicing a dumpster at a local business" className="w-full aspect-[16/9] object-cover" loading="lazy" />
                <div className="p-6 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="h-6 w-6 text-sky-600" />
                    <h3 className="font-heading font-bold text-xl text-slate-900">Commercial Front-Load</h3>
                  </div>
                  <p className="text-slate-600 text-sm">Flexible waste service for local businesses — weekly, multiple times per week, or custom.</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-heading font-semibold text-sky-700 group-hover:gap-2.5 transition-all">
                    Commercial Service <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. Why YWS */}
      <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-200" data-testid="why-yws">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight text-center">Why Yavapai Waste Solutions?</h2>
            <p className="text-center text-slate-600 mt-3 max-w-2xl mx-auto">No surprise fees. No games. Straightforward pricing — and a local team that actually answers the phone.</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Repeat, title: "Same-Day Swaps Available", text: "Full box slowing the job down? Call us — we keep your crew moving." },
              { icon: CalendarCheck, title: "Saturday Swaps Available", text: "For existing rental customers, Saturday swaps keep weekend work on track." },
              { icon: ReceiptText, title: "Pricing Built Around You", text: "Pricing built around how you actually use us. The more you use us, the better we can work with you." },
              { icon: Medal, title: "Local & Veteran Owned", text: "Real people, real accountability. We live and die by our customer service." },
            ].map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08}>
                <div className="bg-white rounded-xl border border-slate-200 p-6 h-full hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`why-${i}`}>
                  <d.icon className="h-7 w-7 text-sky-600 mb-4" />
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{d.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Roll-off sizes */}
      <section className="py-16 sm:py-24 bg-white" data-testid="sizes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-700 mb-3">Roll-Off Dumpsters</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Three Sizes. Zero Guesswork.</h2>
            </div>
            <Link to="/dumpster-size-guide" className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-sky-700 hover:text-sky-800 underline underline-offset-4" data-testid="compare-sizes-link">
              Compare Dumpster Sizes <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
            {ROLLOFF_SIZES.map((s, i) => (
              <Reveal key={s.size} delay={i * 0.08}>
                <DumpsterCard {...s} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="help-me-choose">
              <p className="text-slate-700 text-sm sm:text-base">
                <span className="font-heading font-bold text-slate-900">Not sure what size you need?</span> Tell us what you're working on and we'll help you choose.
              </p>
              <button
                onClick={() => openRequest("Not Sure — Help Me Choose")}
                className="shrink-0 px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-heading font-semibold text-sm transition-all active:scale-[0.98]"
                data-testid="help-me-choose-btn"
              >
                Help Me Choose
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Contractor section */}
      <section className="py-16 sm:py-24 bg-ink text-white" data-testid="contractor-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-400 mb-3">For General Contractors & High-Volume Crews</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Running More Than One Job?</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Your dumpster company shouldn't be the reason your crew is standing around. Keep every job site supplied with long-term rentals, Same-Day Swaps Available, and Saturday Swaps Available — backed by pricing built around how often you use us.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contractor-dumpster-rentals#pricing"
                className="px-7 py-3.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-heading font-semibold text-center transition-all active:scale-[0.98]"
                data-testid="contractor-pricing-cta"
              >
                Get Contractor Pricing
              </Link>
              <Link
                to="/contractor-dumpster-rentals"
                className="px-7 py-3.5 rounded-lg border-2 border-slate-600 hover:border-sky-400 text-white font-heading font-semibold text-center transition-all"
                data-testid="contractor-learn-more"
              >
                Contractor Dumpster Service
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="space-y-4">
              {[
                "Long-term rentals — keep it as long as the job requires",
                "Same-Day Swaps Available & Saturday Swaps Available",
                "Customized, usage-based pricing for high-volume accounts",
                "Transparent pricing — no hidden fees, ever",
                "A local team you can actually reach",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-4">
                  <span className="h-2 w-2 rounded-full bg-sky-400 mt-2 shrink-0" aria-hidden="true" />
                  <span className="text-slate-200">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* 7. How it works */}
      <section className="py-16 sm:py-24 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight text-center">How It Works</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "1", title: "Request Your Dumpster", text: "Tell us the size (or let us help you choose), your ZIP, and when you need it. About a minute, online." },
              { n: "2", title: "We Coordinate Delivery", text: "Nathaniel or Kay confirms your size, placement, and straightforward pricing — no surprise fees." },
              { n: "3", title: "Call When You're Ready", text: "Keep it as long as the job requires. Need a swap? Same-Day Swaps Available for existing customers." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative rounded-xl border border-slate-200 p-7 pt-9 h-full bg-slate-50/50" data-testid={`step-${s.n}`}>
                  <span className="absolute -top-5 left-6 font-heading font-extrabold text-5xl text-sky-600/20 select-none" aria-hidden="true">{s.n}</span>
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-2 relative">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed relative">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Reviews */}
      <ReviewCarousel />

      {/* 9. Front-load overview */}
      <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-200" data-testid="frontload-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img
              src="/assets/yws/uploads/front-load-6-2.jpeg"
              alt="Branded YWS front-load dumpsters staged for service"
              className="rounded-2xl w-full object-cover aspect-[16/10] shadow-xl"
              loading="lazy"
              data-testid="frontload-image"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-700 mb-3">Front-Load Service</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Recurring Waste Service, Handled.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Branded 3–8 yard front-load dumpsters with flexible recurring schedules — weekly, multiple times per week, or custom schedules based on your waste volume.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <Link to="/front-load/residential" className="group rounded-xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid="frontload-residential-card">
                <h3 className="font-heading font-bold text-lg text-slate-900">Residential Front-Load</h3>
                <p className="text-sm text-slate-600 mt-1">Reliable recurring waste service.</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-heading font-semibold text-sky-700 group-hover:gap-2.5 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
              <Link to="/front-load/commercial" className="group rounded-xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid="frontload-commercial-card">
                <h3 className="font-heading font-bold text-lg text-slate-900">Commercial Front-Load</h3>
                <p className="text-sm text-slate-600 mt-1">Flexible waste service for local businesses.</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-heading font-semibold text-sky-700 group-hover:gap-2.5 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 10. Service area map */}
      <section className="py-16 sm:py-24 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-700 mb-3">Service Areas</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Proudly Serving Yavapai County</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Roll-off rentals and front-load service across eight communities. Tap your area for local details.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {SERVICE_AREAS.map((a) => (
                <Link
                  key={a.slug}
                  to={`/service-areas/${a.slug}`}
                  className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-sky-500 hover:text-sky-700 transition-colors"
                  data-testid={`area-chip-${a.slug}`}
                >
                  {a.name}
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ServiceAreaMap />
          </Reveal>
        </div>
      </section>

      {/* 11. Final CTA */}
      <CTASection />
    </>
  );
}
